/**
 * G014 — 存档持久化（IndexedDB 适配器）。
 *
 * 信封的构造与校验在 `simulation-core/save/envelope.ts`（纯逻辑、可无头测试）；
 * 本文件只负责「放到哪、怎么放」，因为 IndexedDB 属于宿主环境，
 * `.claude/rules/simulation.md` 禁止 simulation-core 碰它。
 *
 * 安全提交（验收要求「自动存档失败保留旧档」）：
 * 先写入临时键，读回校验成功后才替换正式键。中途失败或页面被杀，
 * 正式键始终是上一份完整存档 —— 绝不会出现「存到一半、旧档没了」。
 */
import type { SaveEnvelope } from '@crater-zero/simulation-core';

const DB_NAME = 'crater_zero_save_db';     // 与 name-lock.md 固定值一致
const DB_VERSION = 1;
const STORE = 'saves';

/** 存档槽。auto 是自动存档，manual-N 是玩家手动槽。 */
export type SlotId = 'auto' | 'manual-1' | 'manual-2' | 'manual-3';
export const MANUAL_SLOTS: readonly SlotId[] = ['manual-1', 'manual-2', 'manual-3'];
export const ALL_SLOTS: readonly SlotId[] = ['auto', ...MANUAL_SLOTS];

export interface SlotInfo {
  readonly slot: SlotId;
  readonly day: number;
  readonly minuteOfDay: number;
  readonly buildings: number;
  readonly createdAtMs: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB 不可用')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = (): void => { resolve(req.result); };
    req.onerror = (): void => { reject(req.error ?? new Error('打开存档库失败')); };
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    req.onsuccess = (): void => { resolve(req.result); };
    req.onerror = (): void => { reject(req.error ?? new Error('存档操作失败')); };
  }));
}

/**
 * 写入一个槽位（安全提交）。
 *
 * 顺序刻意如此：临时键 → 读回校验 → 正式键 → 删临时键。
 * 任何一步失败都直接抛出，而**正式键此刻仍是上一份完整存档**。
 */
export async function writeSlot(slot: SlotId, env: SaveEnvelope): Promise<void> {
  const tmpKey = `${slot}__tmp`;
  await tx('readwrite', (s) => s.put(env, tmpKey));
  const back = await tx<SaveEnvelope | undefined>('readonly', (s) => s.get(tmpKey));
  if (!back || back.checksum !== env.checksum) {
    throw new Error('存档写入校验失败，旧档已保留');
  }
  await tx('readwrite', (s) => s.put(back, slot));
  await tx('readwrite', (s) => s.delete(tmpKey));
}

export async function readSlot(slot: SlotId): Promise<unknown> {
  return tx<unknown>('readonly', (s) => s.get(slot));
}

export async function deleteSlot(slot: SlotId): Promise<void> {
  await tx('readwrite', (s) => s.delete(slot));
}

/** 列出全部槽位的摘要（空槽返回 null，UI 直接渲染成「空」）。 */
export async function listSlots(): Promise<Array<SlotInfo | null>> {
  const out: Array<SlotInfo | null> = [];
  for (const slot of ALL_SLOTS) {
    try {
      const raw = await readSlot(slot) as SaveEnvelope | undefined;
      out.push(raw
        ? {
          slot,
          day: raw.label?.day ?? 0,
          minuteOfDay: raw.label?.minuteOfDay ?? 0,
          buildings: raw.label?.buildings ?? 0,
          createdAtMs: raw.createdAtMs ?? 0,
        }
        : null);
    } catch {
      out.push(null);          // 单槽损坏不该让整个存档列表打不开
    }
  }
  return out;
}

/** 是否存在任何可继续的存档（主菜单的「继续」是否可用）。 */
export async function hasAnySave(): Promise<boolean> {
  try {
    return (await listSlots()).some((s) => s !== null);
  } catch {
    return false;
  }
}

/** 最近一次存档所在的槽（「继续游戏」读它）。 */
export async function latestSlot(): Promise<SlotId | null> {
  const slots = await listSlots();
  let best: SlotInfo | null = null;
  for (const s of slots) {
    if (s && (!best || s.createdAtMs > best.createdAtMs)) best = s;
  }
  return best?.slot ?? null;
}
